import React, { useState } from 'react';
/*import RatefoldProject from './RatefoldProject';*/
import { motion } from 'framer-motion';
import headshot1 from './assets/images/headshot1.jpg';
// Removed Github, Linkedin, Mail from here
import { ExternalLink, GraduationCap, } from 'lucide-react';
import SpotifyRecommendations from './SpotifyRecommendations';
import GlobalCoalitionSim from './GlobalCoalitionSim';
import MatrixRainAnimation from './MatrixRainAnimation';
import RemoteAccess from './RemoteAccess';

const SectionTitle = ({ children }) => (
  <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
    <span className="text-secondary">/</span> {children}
  </h2>
);

const ProjectCard = ({ title, desc, tags, link, onClick, featured = false }) => (
  <motion.div 
    whileHover={{ y: -10 }}
    className={`p-6 rounded-xl border transition-all ${featured ? 'border-secondary bg-secondary/5' : 'border-slate-700 bg-slate-800/50'} hover:border-secondary`}
  >
    <div className="flex justify-between items-start mb-4">
      <h3 className="text-xl font-bold text-white">{title}</h3>
      {link ? (
        <a href={link} className="text-secondary hover:text-white transition-colors" target="_blank" rel="noreferrer">
          <ExternalLink size={20} />
        </a>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="text-secondary hover:text-white transition-colors"
          aria-label={`View ${title} details`}
        >
          <ExternalLink size={20} />
        </button>
      )}
    </div>
    <p className="text-slate-400 mb-4">{desc}</p>
    <div className="flex flex-wrap gap-2">
      {tags.map(tag => (
        <span key={tag} className="text-xs font-mono text-secondary bg-secondary/10 px-2 py-1 rounded">
          {tag}
        </span>
      ))}
    </div>
  </motion.div>
);

function App() {
  const [activePage, setActivePage] = useState(null);

  const handleOpenPage = (page) => {
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    setActivePage(page);
  };

  const pageMap = {
    spotify: <SpotifyRecommendations onBack={() => setActivePage(null)} />,
    coalition: <GlobalCoalitionSim onBack={() => setActivePage(null)} />,
    matrix: <MatrixRainAnimation onBack={() => setActivePage(null)} />,
    remote: <RemoteAccess onBack={() => setActivePage(null)} />,
  };

  if (activePage) {
    return pageMap[activePage] || null;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-20">
      {/* HERO SECTION */}
      <header className="mb-32">
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-secondary font-mono mb-5"
        >
          Hi, my name is
        </motion.p>
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-6xl md:text-8xl font-bold text-white mb-6"
        >
          Ethan Klassen.
        </motion.h1>
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-4xl md:text-6xl font-bold text-slate-400 mb-8"
        >
          I build things for the web & mobile.
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-xl text-lg text-slate-400 mb-12"
        >
          4th year Software Engineering student at the University of Calgary with a passion for building polished, 
          scalable digital products. I specialize in full-stack development and cross-platform mobile applications using Flutter. 
          Currently developing <a href="https://ratefold.com" target="_blank" rel="noreferrer" className="text-secondary hover:text-white transition-colors">Ratefold</a>: a modern music discovery, rating, and social platform inspired by 
          Letterboxd and RateYourMusic, designed with a mobile-first experience and clean UI at its core.
        </motion.p>
        <motion.div 
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ delay: 0.4 }}
  className="flex gap-6"
>
  {/* GitHub Icon */}
  <a href="https://github.com/ethan-klassen" className="text-slate-400 hover:text-secondary transition-colors" aria-label="GitHub">
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
      <path d="M9 18c-4.51 2-5-2-7-2" />
    </svg>
  </a>

  {/* LinkedIn Icon */}
  <a href="https://www.linkedin.com/in/ethan-klassen-4428aa2b6" className="text-slate-400 hover:text-secondary transition-colors" aria-label="LinkedIn">
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  </a>

  {/* Mail Icon */}
  <a href="mailto:ethanklassen7@gmail.com" className="text-slate-400 hover:text-secondary transition-colors" aria-label="Email">
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="16" x="2" y="4" r="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  </a>
</motion.div>
      </header>

      {/* ABOUT SECTION */}
      <section className="mb-32">
        <SectionTitle>About Me</SectionTitle>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2 text-slate-400 space-y-4">
            <p>
              Hello! I'm a Software Engineering student who has spent the last several years building applications across web, 
              backend, and mobile platforms. My interest in development started with frontend design and quickly evolved 
              into a deeper focus on full-stack architecture, scalable systems, and creating products that feel intuitive to use.
            </p>
            <p>
              Recently, I've been heavily focused on cross-platform development with Flutter and modern backend infrastructure 
              using Supabase and PostgreSQL. I enjoy designing systems end-to-end, from database architecture and APIs to 
              responsive UI and user experience.
            </p>
            <p>
              Outside of coursework, I spend most of my time building independent projects, refining product ideas, 
              and exploring ways to combine clean design with strong engineering foundations.
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm mt-6">
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Flutter/Dart</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Typescript</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> React/Next.js</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Node.js</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> PostgreSQL</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Supabase</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Tailwind CSS</li>
              <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> REST APIs</li>

            </ul>
          </div>
          <div className="relative group mx-auto w-64 h-64">
            <div className="absolute inset-0 border-2 border-secondary rounded-xl translate-x-4 translate-y-4 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform" />
            <div className="absolute inset-0 bg-slate-700 rounded-xl overflow-hidden">
              <img src={headshot1} alt="Profile" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all" />
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTS SECTION */}
      <section className="mb-32">
        <SectionTitle>Featured Work</SectionTitle>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Ratefold - The Featured Project */}
          <ProjectCard 
            featured={true}
            title="Ratefold" 
            desc="A modern music discovery and social rating platform inspired by Letterboxd, Album of the Year, and RateYourMusic. Built with a mobile-first philosophy using Flutter and Supabase, allowing users to rate albums, write reviews, build lists, discover music through community activity, and track listening habits through a polished cross-platform experience." 
            tags={['Flutter', 'Dart', 'Supabase', 'PostgreSQL', 'TypeScript Edge Functions','MusicBrainz API', 'Cover Art Archive', 'Last.fm API']}
            link="https://ratefold.com" 
          />
          <ProjectCard 
            title="Remote Access PC-Mobile Controller" 
            desc="A cross-platform mobile and Windows application that allows users to remotely control their PC using a smartphone. Features include a virtual touchpad using the mobile screen, keyboard input, and on-screen joystick controls for easy gaming." 
            tags={['JavaScript', 'Python', 'Socket Programming', 'GUI', 'Windows API', 'Video Encoding', 'Lightweight Performance','Cross-Platform Development']}
            onClick={() => handleOpenPage('remote')} 
          />
          <ProjectCard 
            title="Spotify Personal Recommendation Engine" 
            desc="A machine learning-based song recommendation system that recommends songs similar to the inputted track based on my own personal listening history, data, and preferences." 
            tags={['React', 'Vite', 'Python', 'Last.fm API', 'Spotify API', 'Cosine Similarity', 'Web Audio API', 'Tailwind CSS']}
            onClick={() => handleOpenPage('spotify')} 
          />
          <ProjectCard 
            title="Global Coalition Sim -- SDG Decision Game" 
            desc="Online playable game where users make decisions to keep the world in economic, social, and environmental balance. Group Project for Software Engineering course at University of Calgary. Built with React TypeScript, Tailwind CSS, Flask, and PostgreSQL. I developed the frontend interface, animations, and audio design, including music and SFX to support dynamic game scenarios and user interactions."
            tags={['React', 'TypeScript', 'Tailwind CSS', 'PostgreSQL', 'Audio Design', 'Dynamic Audio Implementation']}
            onClick={() => handleOpenPage('coalition')} 
          />
          <ProjectCard 
            title="Falling Matrix Rain Animation for Browser" 
            desc="A visually striking animation that simulates the iconic falling code rain from The Matrix movie, implemented using JavaScript and CSS for smooth performance in modern browsers." 
            tags={['JavaScript', 'HTML/CSS', 'Animation']}
            onClick={() => handleOpenPage('matrix')} 
          />
        </div>
      </section>

      {/* EDUCATION SECTION */}
      <section className="mb-32">
        <SectionTitle>Education</SectionTitle>
        <div className="flex items-start gap-4 p-6 rounded-xl bg-slate-800/30 border border-slate-700">
          <GraduationCap className="text-secondary shrink-0" size={32} />
          <div>
            <h3 className="text-xl font-bold text-white">B.Sc. in Software Engineering</h3>
            <p className="text-slate-400">University of Calgary | 2023 — Present</p>
            <p className="text-sm text-slate-500 mt-2 italic">Currently in 4th Year. Areas of interest include:
              <ul className="grid grid-cols-3 gap-2 font-mono text-sm mt-4">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Mobile Application Development</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Distributed Systems</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Backend Architecture</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> UI/UX Engineering</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-secondary rounded-full"/> Database Systems</li>
            </ul>
          </p>
          </div>
        </div>
      </section>

      {/* RATEFOLD PROJECT */}
      {/*<RatefoldProject /> {/* Import and use the RatefoldProject component here */}

      {/* FOOTER */}
      <footer className="text-center text-slate-500 font-mono text-sm">
        <p>Designed & Built by Ethan Klassen &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

export default App;